import type {
  IrrigationRepairRow,
  IrrigationScheduleBlock,
  SiteMapBlock,
} from '@lib/dto/proposal-document.dto'
import { treePriority, type TreePriority } from '@lib/helpers/proposal-plans'

/**
 * Irrigation repairs as the plan draws them: numbered, colored by how urgent
 * they are, and kept apart when several stand on one spot.
 */

/**
 * A repair's number on the plan and in the appendix: "R1", "R2", in the
 * order the schedule lists them, which is frozen with the document. The R
 * keeps it from reading as a tree's number on a plan that shows both.
 */
export function repairLabel(index: number): string {
  return `R${String(index + 1)}`
}

/** Each repair's number at one site, by key. */
export function repairLabels(
  schedule: IrrigationScheduleBlock | undefined,
): Map<string, string> {
  return new Map(
    (schedule?.repairs ?? []).map((repair, index) => [repair.key, repairLabel(index)]),
  )
}

/** Urgency by severity, read as a tree's is, so one legend serves both. */
export function repairPriority(repair: IrrigationRepairRow): TreePriority {
  return treePriority(repair.severity)
}

/** A pinned repair with its schedule row, for drawing. */
export interface PlacedRepair {
  key: string
  label: string
  latitude: number
  longitude: number
  repair: IrrigationRepairRow
  /** Where it sits around the spot it shares, in mark widths. Zero alone. */
  dx: number
  dy: number
}

/**
 * The site's pinned repairs, joined to their rows, with any that share a
 * spot spread in a ring around it.
 *
 * Three problems on one valve are three pins on the same point, and stacked
 * they read as one. The spread is in mark widths, not meters, so it is the
 * same on a page and at any zoom on the live map. A pin whose row is not on
 * the schedule is left off: there is nothing to say about it.
 */
export function placedRepairs(
  block: SiteMapBlock,
  schedule: IrrigationScheduleBlock | undefined,
): PlacedRepair[] {
  const rows = new Map((schedule?.repairs ?? []).map((repair) => [repair.key, repair]))
  const labels = repairLabels(schedule)
  const placed = (block.irrigation?.repairs ?? []).flatMap((pin) => {
    const repair = rows.get(pin.key)
    const label = labels.get(pin.key)
    return repair === undefined || label === undefined
      ? []
      : [{ ...pin, label, repair, dx: 0, dy: 0 }]
  })

  const bySpot = new Map<string, PlacedRepair[]>()
  for (const entry of placed) {
    const spot = `${entry.latitude.toFixed(6)},${entry.longitude.toFixed(6)}`
    bySpot.set(spot, [...(bySpot.get(spot) ?? []), entry])
  }
  for (const group of bySpot.values()) {
    if (group.length < 2) continue
    group.forEach((entry, index) => {
      const angle = (2 * Math.PI * index) / group.length - Math.PI / 2
      // Far enough apart that neighbors touch rather than overlap.
      const reach = 0.5 / Math.sin(Math.PI / Math.max(group.length, 2)) + 0.1
      entry.dx = Math.cos(angle) * reach
      entry.dy = Math.sin(angle) * reach
    })
  }
  return placed
}
